import React, { useCallback, useEffect, useState } from 'react';
import {
  Activity,
  CheckCircle2,
  XCircle,
  RefreshCw,
  AlertTriangle,
  MinusCircle,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { money } from '@/data/constants';
import { WEBHOOK_PLAN_CREDITS } from '@/data/webhookSetup';

/**
 * Admin > Webhook & recovery — LIVE ACTIVATION CHAIN
 *
 * Answers one question at a glance for the newest GENUINE membership payment:
 *
 *   payment -> membership activated -> credits assigned
 *           -> dashboard notification -> membership activated email
 *
 * Synthetic traffic is excluded, because a self-test proving itself is worth
 * nothing. An event is only treated as real when its id does NOT carry a
 * platform-generated prefix and the recipient is not a reserved test domain:
 *
 *   giggone-selftest-       loopback delivery from payment-webhook
 *   giggone-acceptance-     payment-acceptance-test fixtures
 *   giggone-admin-recovery- a hand-made admin grant, not CRM money
 *   conc-                   credit-concurrency-test fixtures
 *   *.invalid / *.test      reserved fixture recipients
 *
 * Every row is read back from the database the customer's own dashboard reads,
 * so this panel cannot report success unless the customer really has it.
 */

const SYNTHETIC_PREFIXES = [
  'giggone-selftest-',
  'giggone-webhook-test-',
  'giggone-acceptance-',
  'giggone-admin-recovery-',
  'conc-',
];

const TEST_EMAIL = /(\.invalid|@concurrency\.test|\.test)$/i;

const isSynthetic = (eventId: string, email: string | null) =>
  SYNTHETIC_PREFIXES.some((p) => eventId.startsWith(p)) || (email ? TEST_EMAIL.test(email) : false);

interface EventRow {
  id: string;
  event_id: string;
  event_type: string;
  plan_code: string | null;
  email: string | null;
  user_id: string | null;
  amount: number;
  result: string | null;
  created_at: string;
}

type CheckState = 'pass' | 'fail' | 'warn';

interface Check {
  label: string;
  state: CheckState;
  detail: string;
}

const AdminActivationChain: React.FC = () => {
  const { lang } = useI18n();
  const es = lang === 'es';

  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState<EventRow | null>(null);
  const [checks, setChecks] = useState<Check[]>([]);
  const [syntheticOnly, setSyntheticOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setChecks([]);
    setEvent(null);

    const { data } = await supabase
      .from('mkt_payment_events')
      .select('id,event_id,event_type,plan_code,email,user_id,amount,result,created_at')
      .order('created_at', { ascending: false })
      .limit(60);

    const rows = (data as EventRow[]) || [];
    const real = rows.find(
      (r) =>
        !isSynthetic(r.event_id, r.email) &&
        ['activated', 'renewed', 'plan_changed', 'reconciled:manual_grant'].includes(String(r.result))
    );

    if (!real) {
      setSyntheticOnly(rows.length > 0);
      setLoading(false);
      return;
    }

    setSyntheticOnly(false);
    setEvent(real);

    const expected = real.plan_code ? WEBHOOK_PLAN_CREDITS[real.plan_code] ?? null : null;
    const reconciled = String(real.result) === 'reconciled:manual_grant';

    const [payment, subscription, ledger, profile, emailLog, notice] = await Promise.all([
      supabase
        .from('mkt_payments')
        .select('amount,status,description')
        .eq('external_payment_id', real.event_id)
        .limit(1)
        .then((r) => r.data?.[0] || null),
      supabase
        .from('mkt_subscriptions')
        .select('plan_code,status,included_leads,leads_used,payments_made,term_months')
        .eq('last_event_id', real.event_id)
        .limit(1)
        .then((r) => r.data?.[0] || null),
      supabase
        .from('mkt_credit_ledger')
        .select('id,delta,kind')
        .eq('note', `${real.plan_code} activation · transaction ${real.event_id}`)
        .then((r) => r.data || []),
      real.user_id
        ? supabase
            .from('mkt_profiles')
            .select('credits,extra_credits,email')
            .eq('id', real.user_id)
            .limit(1)
            .then((r) => r.data?.[0] || null)
        : Promise.resolve(null),
      real.user_id
        ? supabase
            .from('mkt_email_log')
            .select('status,to_email,created_at,error')
            .eq('template', 'membership_activated')
            .eq('user_id', real.user_id)
            .order('created_at', { ascending: false })
            .limit(1)
            .then((r) => r.data?.[0] || null)
        : Promise.resolve(null),
      real.user_id
        ? supabase
            .from('mkt_notifications')
            .select('created_at,read_at')
            .eq('kind', 'membership_activated')
            .eq('user_id', real.user_id)
            .order('created_at', { ascending: false })
            .limit(1)
            .then((r) => r.data?.[0] || null)
        : Promise.resolve(null),
    ]);

    const list: Check[] = [];

    list.push({
      label: es ? 'Evento real del CRM (no sintético)' : 'Real CRM event (not synthetic)',
      state: 'pass',
      detail: real.event_id,
    });
    list.push({
      label: es ? 'Resultado del webhook' : 'Webhook result',
      state: 'pass',
      detail: String(real.result),
    });
    list.push({
      label: es ? 'Registro de pago creado' : 'Payment record created',
      state: payment ? 'pass' : 'fail',
      detail: payment ? `${money(payment.amount || 0)} · ${payment.status}` : es ? 'falta' : 'missing',
    });
    list.push({
      label: es ? 'Suscripción activada' : 'Subscription activated',
      state: subscription ? 'pass' : 'fail',
      detail: subscription
        ? `${subscription.plan_code} · ${subscription.status} · ${subscription.included_leads} ${
            es ? 'incluidos' : 'included'
          }`
        : es
        ? 'falta'
        : 'missing',
    });

    if (reconciled) {
      list.push({
        label: es ? 'Créditos (reconciliación)' : 'Credits (reconciliation)',
        state: 'pass',
        detail: es
          ? 'Sin nueva concesión — el mismo dinero ya se había otorgado a mano'
          : 'No new grant — the same money was already granted by hand',
      });
    } else {
      const grants = ledger.filter((l: any) => l.kind === 'plan_grant');
      list.push({
        label: es ? 'Un solo asiento plan_grant' : 'Exactly one plan_grant ledger entry',
        state: grants.length === 1 ? 'pass' : 'fail',
        detail: `${grants.length} · ${grants[0]?.delta ?? 0} ${es ? 'créditos' : 'credits'}`,
      });
      list.push({
        label: es ? 'Créditos asignados' : 'Credits assigned',
        state: profile && expected !== null && profile.credits === expected ? 'pass' : 'fail',
        detail:
          profile === null
            ? es
              ? 'perfil no leído'
              : 'profile unreadable'
            : `${profile.credits} / ${expected ?? '—'} ${es ? 'esperados' : 'expected'}`,
      });
    }

    list.push({
      label: es ? 'Aviso en el panel del cliente' : 'Customer dashboard notification',
      state: notice ? 'pass' : 'fail',
      detail: notice
        ? `${new Date(notice.created_at).toLocaleString()}${
            notice.read_at ? ` · ${es ? 'leído' : 'read'}` : ''
          }`
        : es
        ? 'no escrito'
        : 'not written',
    });

    list.push({
      label: es ? 'Correo de membresía activada' : 'Membership activated email',
      state: !emailLog ? 'fail' : emailLog.status === 'sent' || emailLog.status === 'delivered' ? 'pass' : 'warn',
      detail: emailLog
        ? `${emailLog.status} · ${emailLog.to_email}${emailLog.error ? ` · ${emailLog.error.slice(0, 60)}` : ''}`
        : es
        ? 'no enviado'
        : 'not sent',
    });

    list.push({
      label: es ? '¿Hizo falta recuperación manual?' : 'Manual recovery required?',
      state: reconciled ? 'warn' : 'pass',
      detail: reconciled
        ? es
          ? 'Sí — esta venta se otorgó a mano y después se reconcilió'
          : 'Yes — this sale was granted by hand and later reconciled'
        : es
        ? 'No — automático de principio a fin'
        : 'No — automatic end to end',
    });

    setChecks(list);
    setLoading(false);
  }, [es]);

  useEffect(() => {
    load();
  }, [load]);

  const icon = (s: CheckState) =>
    s === 'pass' ? (
      <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
    ) : s === 'warn' ? (
      <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
    ) : (
      <XCircle className="h-4 w-4 shrink-0 text-red-600" />
    );

  const failures = checks.filter((c) => c.state === 'fail').length;

  return (
    <div className="mt-14 border-t border-slate-200 pt-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-xl font-extrabold text-slate-900">
          <Activity className="h-5 w-5 text-orange-500" />
          {es ? 'Cadena de activación en vivo' : 'Live activation chain'}
        </h2>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          style={{ touchAction: 'manipulation' }}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          {es ? 'Actualizar' : 'Refresh'}
        </button>
      </div>
      <p className="mt-1 max-w-3xl text-sm text-slate-500">
        {es
          ? 'Comprueba la venta real más reciente de extremo a extremo: pago → membresía → créditos → aviso en el panel → correo. Los eventos de prueba (selftest, acceptance, concurrency, recuperación manual) se excluyen.'
          : 'Checks the most recent genuine sale end to end: payment → membership → credits → dashboard notice → email. Test traffic (selftest, acceptance, concurrency, manual grants) is excluded.'}
      </p>

      {loading ? (
        <p className="mt-4 rounded-2xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
          {es ? 'Comprobando…' : 'Checking…'}
        </p>
      ) : !event ? (
        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50/70 p-5">
          <p className="flex items-center gap-2 text-sm font-extrabold text-amber-900">
            <MinusCircle className="h-4 w-4" />
            {es ? 'Todavía no hay ninguna venta real' : 'No genuine sale yet'}
          </p>
          <p className="mt-1 text-[13px] leading-relaxed text-amber-900">
            {syntheticOnly
              ? es
                ? 'Solo se han recibido entregas sintéticas (selftest / acceptance / concurrency / recuperación manual). La primera compra real de Basic, Pro o Premium aparecerá aquí automáticamente con las nueve comprobaciones.'
                : 'Only synthetic deliveries have been received so far (selftest / acceptance / concurrency / manual recovery). The first real Basic, Pro or Premium purchase will appear here automatically with all nine checks.'
              : es
              ? 'Aún no ha llegado ningún evento de pago.'
              : 'No payment event has arrived yet.'}
          </p>
        </div>
      ) : (
        <div
          className={`mt-4 rounded-2xl border p-5 ${
            failures === 0 ? 'border-emerald-200 bg-emerald-50/70' : 'border-red-200 bg-red-50/70'
          }`}
        >
          <p className="text-sm font-extrabold text-slate-900">
            {event.plan_code ? event.plan_code.toUpperCase() : '—'} · {money(event.amount || 0)} ·{' '}
            {event.email || '—'}
          </p>
          <p className="mt-0.5 font-mono text-xs text-slate-500">
            {event.event_id} · {new Date(event.created_at).toLocaleString()}
          </p>

          <dl className="mt-4 grid gap-x-6 gap-y-1.5 text-[13px] sm:grid-cols-2">
            {checks.map((c) => (
              <div key={c.label} className="flex items-start justify-between gap-3 border-b border-white/70 py-1.5">
                <dt className="flex items-center gap-2 font-bold text-slate-700">
                  {icon(c.state)}
                  {c.label}
                </dt>
                <dd className="text-right font-semibold text-slate-900">{c.detail}</dd>
              </div>
            ))}
          </dl>

          <p className="mt-3 text-[13px] font-bold text-slate-700">
            {failures === 0
              ? es
                ? 'Cadena completa: el cliente tiene membresía, créditos, aviso en el panel y correo.'
                : 'Chain complete: the customer has the membership, the credits, the dashboard notice and the email.'
              : es
              ? `${failures} comprobación(es) fallida(s) — investigue antes de la siguiente venta.`
              : `${failures} check(s) failed — investigate before the next sale.`}
          </p>
        </div>
      )}
    </div>
  );
};

export default AdminActivationChain;
